import React, { useEffect, useState, useCallback } from 'react';
import { Calendar, Users, Clock, Check, X, MapPin, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../lib/api';
import { toast } from 'sonner';

const fmtDate = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
const today = () => new Date().toISOString().slice(0, 10);

const STATUS_COLORS = {
  confirmed: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  seated: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  arrived: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  completed: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  no_show: 'bg-red-500/15 text-red-400 border-red-500/30',
  cancelled: 'bg-zinc-700/15 text-zinc-600 border-zinc-700/30 line-through',
};

export default function ReservationsPage() {
  const [date, setDate] = useState(today());
  const [statusFilter, setStatusFilter] = useState('');
  const [reservations, setReservations] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resCfg, setResCfg] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newRes, setNewRes] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    date: today(), hour: 18, party_size: 2, notes: '',
  });
  const [creating, setCreating] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (date) params.set('date', date);
      if (statusFilter) params.set('status_filter', statusFilter);
      const [r, t, cfg] = await Promise.all([
        api.get(`/reservations?${params}`),
        api.get('/tables'),
        api.get('/reservation-settings'),
      ]);
      setReservations(r.data || []);
      setTables(t.data || []);
      setResCfg(cfg.data || null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  }, [date, statusFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const assignTable = async (reservationId, tableId) => {
    try {
      await api.put(`/reservations/${reservationId}/assign-table`, { table_id: tableId });
      toast.success('Table assigned');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const mark = async (reservationId, status) => {
    try {
      await api.put(`/reservations/${reservationId}/mark`, { status });
      toast.success(`Marked ${status}`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const createReservation = async () => {
    if (!newRes.customer_name.trim()) { toast.error('Customer name required'); return; }
    setCreating(true);
    try {
      await api.post('/reservations/manager', newRes);
      toast.success('Reservation booked');
      setCreateOpen(false);
      setNewRes({ customer_name: '', customer_email: '', customer_phone: '', date: today(), hour: 18, party_size: 2, notes: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setCreating(false); }
  };

  // Group by hour for the day view
  const grouped = reservations.reduce((acc, r) => {
    const key = `${r.date} ${r.slot_label}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  // Unassigned: confirmed/seated/arrived bookings that don't have a table_id yet.
  // Cancelled / no-show / completed are excluded.
  const unassignedCount = reservations.filter(r =>
    !r.table_id && !['cancelled', 'no_show', 'completed'].includes(r.status)
  ).length;

  const dateOptions = (() => {
    const arr = [];
    const start = new Date();
    start.setDate(start.getDate() - 1);
    for (let i = 0; i < 14; i++) { const d = new Date(start); d.setDate(start.getDate() + i); arr.push(d.toISOString().slice(0, 10)); }
    return arr;
  })();

  return (
    <div className="space-y-5 min-w-0 max-w-full overflow-hidden" data-testid="reservations-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-serif text-white">Reservations</h1>
          <p className="text-xs text-zinc-500 font-sans uppercase tracking-[0.15em] mt-1">{reservations.length} booking{reservations.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="h-9 bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 rounded-sm font-sans text-xs"
            data-testid="add-reservation-button"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add reservation
          </Button>
          <Select value={statusFilter || 'all'} onValueChange={v => setStatusFilter(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[140px] bg-white/[0.03] border border-white/10 text-white text-sm h-9" data-testid="reservations-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-bg-secondary border border-white/10">
              <SelectItem value="all">All status</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="seated">Seated</SelectItem>
              <SelectItem value="arrived">Arrived</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="no_show">No-show</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Date strip — explicit max-w + overflow-x-auto keeps horizontal scroll
          INSIDE the strip on mobile, so the page itself doesn't widen */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar max-w-full -mx-1 px-1">
        {dateOptions.map(d => (
          <button key={d} onClick={() => setDate(d)}
            className={`shrink-0 px-3 py-2 rounded-sm text-xs font-sans border ${date === d ? 'bg-gold/15 text-gold border-gold/40' : 'bg-white/[0.03] border-white/10 text-zinc-300'}`}
            data-testid={`reservations-date-${d}`}>
            {fmtDate(d)}
          </button>
        ))}
      </div>

      {/* Unassigned-table alert */}
      {unassignedCount > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-sm p-3 flex items-center gap-3" data-testid="unassigned-alert">
          <div className="shrink-0 w-8 h-8 rounded-sm bg-amber-500/20 flex items-center justify-center">
            <MapPin className="w-4 h-4 text-amber-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-amber-300 font-sans font-medium">
              {unassignedCount} booking{unassignedCount !== 1 ? 's' : ''} need{unassignedCount === 1 ? 's' : ''} a table
            </p>
            <p className="text-[11px] text-amber-200/60 font-sans">Assign a table from the dropdown — the customer is emailed automatically.</p>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-zinc-500 font-sans text-sm py-8">Loading…</p>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="bg-white/[0.03] border border-white/5 rounded-sm p-6 sm:p-10 text-center">
          <Calendar className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 font-sans text-sm">No reservations for {fmtDate(date)}</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([key, list]) => (
            <div key={key}>
              <h2 className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-sans font-bold mb-2 flex items-center gap-2">
                <Clock className="w-3 h-3" /> {key.split(' ').slice(1).join(' ')}
                <span className="text-zinc-600">· {list.reduce((s, r) => s + (r.party_size || 0), 0)} pax</span>
              </h2>
              <div className="space-y-2">
                {list.map(r => {
                  const needsTable = !r.table_id && !['cancelled', 'no_show', 'completed'].includes(r.status);
                  return (
                  <div key={r.id} className={`rounded-sm p-4 ${needsTable ? 'bg-amber-500/[0.05] border border-amber-500/30' : 'bg-white/[0.03] border border-white/5'}`} data-testid={`reservation-${r.id}`}>
                    <div className="flex justify-between items-start gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-serif text-white text-base">{r.customer_name || 'Guest'}</span>
                          <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-sans ${STATUS_COLORS[r.status] || 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30'}`}>
                            {r.status}
                          </span>
                          {needsTable && (
                            <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/20 text-amber-300 border border-amber-500/40 text-[10px] uppercase tracking-wider font-sans font-bold animate-pulse" data-testid={`needs-table-badge-${r.id}`}>
                              Needs table
                            </span>
                          )}
                          <span className="text-[10px] text-zinc-600 font-mono uppercase">#{r.confirmation_code}</span>
                        </div>
                        <p className="text-xs text-zinc-400 font-sans mt-1 flex items-center gap-3 flex-wrap">
                          <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.party_size}</span>
                          {r.customer_phone && <span>{r.customer_phone}</span>}
                          {r.table_number ? (
                            <span className="text-gold flex items-center gap-1"><MapPin className="w-3 h-3" />Table {r.table_number}</span>
                          ) : (
                            <span className="text-amber-400 flex items-center gap-1"><MapPin className="w-3 h-3" />No table</span>
                          )}
                          {r.preorder_items?.length > 0 && (
                            <span className="text-emerald-400">{r.preorder_items.length} pre-order item{r.preorder_items.length !== 1 ? 's' : ''} · ${r.total.toFixed(2)}</span>
                          )}
                          {r.payment_method === 'card_upfront' && <span className="text-blue-400">{r.payment_status === 'paid' ? 'Paid' : 'Pay upfront'}</span>}
                        </p>
                        {r.notes && <p className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2 py-1 mt-2 font-sans" data-testid={`reservation-notes-${r.id}`}>{r.notes}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={r.table_id || 'none'} onValueChange={v => v !== 'none' && assignTable(r.id, v)}>
                          <SelectTrigger className={`w-[110px] bg-transparent text-white text-xs h-8 ${needsTable ? 'border border-amber-500/50 hover:border-amber-400 ring-1 ring-amber-500/20' : 'border border-white/10'}`} data-testid={`assign-table-${r.id}`}>
                            <SelectValue placeholder="Assign…" />
                          </SelectTrigger>
                          <SelectContent className="bg-bg-secondary border border-white/10">
                            <SelectItem value="none" disabled>Choose table</SelectItem>
                            {tables.map(t => <SelectItem key={t.id} value={t.id}>Table {t.number} (cap {t.capacity})</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {r.status === 'confirmed' && (
                          <Button size="sm" onClick={() => mark(r.id, 'arrived')} className="h-8 bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20 rounded-sm font-sans text-xs" data-testid={`mark-arrived-${r.id}`}>
                            <Check className="w-3 h-3 mr-1" /> Arrived
                          </Button>
                        )}
                        {(r.status === 'confirmed' || r.status === 'arrived') && (
                          <Button size="sm" onClick={() => mark(r.id, 'no_show')} className="h-8 bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 rounded-sm font-sans text-xs" data-testid={`mark-no-show-${r.id}`}>
                            <X className="w-3 h-3 mr-1" /> No-show
                          </Button>
                        )}
                        {r.status === 'arrived' && (
                          <Button size="sm" onClick={() => mark(r.id, 'completed')} className="h-8 bg-zinc-500/10 text-zinc-300 border border-white/10 hover:bg-white/5 rounded-sm font-sans text-xs" data-testid={`mark-completed-${r.id}`}>
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create reservation dialog (manager) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-bg-secondary border border-white/10 max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-white">New reservation</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-1">
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Customer name</Label>
              <Input value={newRes.customer_name} onChange={e => setNewRes(p => ({ ...p, customer_name: e.target.value }))} className="bg-transparent border border-white/10 text-white h-10 font-sans text-sm" placeholder="Jane Smith" data-testid="new-res-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Email</Label>
                <Input value={newRes.customer_email} onChange={e => setNewRes(p => ({ ...p, customer_email: e.target.value }))} className="bg-transparent border border-white/10 text-white h-10 font-sans text-sm" placeholder="optional" data-testid="new-res-email" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Mobile</Label>
                <Input value={newRes.customer_phone} onChange={e => setNewRes(p => ({ ...p, customer_phone: e.target.value }))} className="bg-transparent border border-white/10 text-white h-10 font-sans text-sm" placeholder="+61…" data-testid="new-res-phone" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5 col-span-1">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Date</Label>
                <Input type="date" value={newRes.date} onChange={e => setNewRes(p => ({ ...p, date: e.target.value }))} className="bg-transparent border border-white/10 text-white h-10 font-sans text-sm" data-testid="new-res-date" />
              </div>
              <div className="space-y-1.5 col-span-1">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Hour</Label>
                <select value={newRes.hour} onChange={e => setNewRes(p => ({ ...p, hour: parseInt(e.target.value, 10) }))} className="w-full bg-transparent border border-white/10 text-white h-10 px-2 rounded-sm font-sans text-sm" data-testid="new-res-hour">
                  {(resCfg?.slot_hours || [18,19,20,21,22,23]).map(h => (
                    <option key={h} value={h} className="bg-bg-primary">{h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5 col-span-1">
                <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Party</Label>
                <Input type="number" min={1} max={20} value={newRes.party_size} onChange={e => setNewRes(p => ({ ...p, party_size: parseInt(e.target.value, 10) || 1 }))} className="bg-transparent border border-white/10 text-white h-10 font-sans text-sm" data-testid="new-res-party" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-sans">Notes</Label>
              <Input value={newRes.notes} onChange={e => setNewRes(p => ({ ...p, notes: e.target.value }))} className="bg-transparent border border-white/10 text-white h-10 font-sans text-sm" placeholder="Birthday, dietary, etc." data-testid="new-res-notes" />
            </div>
            <p className="text-[10px] text-zinc-500 font-sans">Phone-in booking — no card charged; deposit settled at the door. Customer can view/cancel via their email if provided.</p>
            <Button onClick={createReservation} disabled={creating} className="w-full bg-gold text-black font-bold rounded-sm font-sans hover:bg-gold-light" data-testid="create-reservation-confirm">
              {creating ? 'Saving…' : 'Create reservation'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
